/**
 * Fase 1 — afgevangen fouten zichtbaar maken in de BESTAANDE Sentry-integratie.
 *
 * AANLEIDING (07-08-2026). Drie storingen op één dag waren alleen in de Supabase-logs
 * te zien: een intake die met HTTP 400 faalde zonder te zeggen waarom, een gebruiker
 * die niet kon inloggen terwijl zijn account gezond was, en een verzoeklus van ~360
 * aanroepen per minuut. Geen ervan bereikte Sentry, want alle drie werden ze door
 * onze eigen code AFGEVANGEN — melding tonen, klaar. Sentry's automatische vangnet
 * ziet alleen ONafgevangen fouten.
 *
 * GEEN TWEEDE INFRASTRUCTUUR. Dit is een dunne laag op `@sentry/react` dat al
 * draait; de serverkant houdt `_shared/errorReporting.ts`. Twee runtimes, één dienst.
 *
 * DE TWEE DINGEN DIE HIER HARD MOETEN ZIJN
 *  1. GEEN PERSOONSGEGEVENS. Collectr verwerkt debiteurgegevens. Context gaat via
 *     een ALLOW-LIST — wat er niet in staat, gaat niet mee — en elke tekst gaat
 *     door de scrubber. Een allow-list faalt dicht; een deny-list vergeet je.
 *  2. GEEN ALARMMOEHEID. Een geldige afwijzing is geen storing. Zakelijke validatie
 *     en autorisatie worden bewust NIET als fout gemeld; alleen onverwacht technisch
 *     falen en operationeel relevante beveiligingsafwijkingen.
 */
import {
  classificeerFout,
  TOEGESTANE_SLEUTELS,
  type Ernst,
  type ObservabilityContext,
} from "./observabilityClassification";
import { scrubMessage } from "@collectr/observability";
import { Sentry } from "./sentry";

function bouwTags(context: ObservabilityContext): Record<string, string> {
  const tags: Record<string, string> = {};
  for (const sleutel of TOEGESTANE_SLEUTELS) {
    const waarde: ObservabilityContext[typeof sleutel] = context[sleutel];
    if (waarde === null || waarde === undefined || waarde === "") continue;
    tags[sleutel] = scrubMessage(String(waarde)).slice(0, 200);
  }
  return tags;
}

/**
 * Meldt een AFGEVANGEN fout aan Sentry wanneer die operationeel relevant is.
 *
 * Geeft terug of er gemeld is — zodat een test de dosering kan toetsen in plaats
 * van alleen dat de functie bestaat.
 *
 * Breekt nooit de aanroeper: monitoring is een neveneffect.
 */
export function reportHandledError(error: unknown, context: ObservabilityContext): boolean {
  try {
    const klasse = classificeerFout(context);
    if (klasse === "business") return false;

    const ernst: Ernst = klasse === "security" || klasse === "operational" ? "warning" : "error";
    const oorspronkelijk = error instanceof Error ? error : new Error(String(error));
    // Een NIEUWE fout met een gescrubde melding: de oorspronkelijke boodschap kan
    // een e-mailadres of IBAN dragen en die mag de deur niet uit.
    const veilig = new Error(scrubMessage(oorspronkelijk.message || "onbekende fout"));
    veilig.name = oorspronkelijk.name || "Error";
    if (oorspronkelijk.stack) veilig.stack = scrubMessage(oorspronkelijk.stack);

    Sentry.captureException(veilig, {
      level: ernst,
      tags: bouwTags(context),
      // GEEN `extra`: dat is precies de plek waar vrije inhoud binnensluipt.
    });
    return true;
  } catch {
    // Monitoring mag nooit de gebruikersactie breken.
    return false;
  }
}
